import React, { useState } from 'react';
import { Lock, Download, FileText, Eye, EyeOff } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, PDFPreview, ToolActionButtons, NoteCard } from '../shared';
import { PDFDocument } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const ProtectPDF = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [permissions, setPermissions] = useState({
        printing: true,
        modifying: false,
        copying: false,
        annotating: false
    });
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');

    const handleFileSelected = (files) => {
        if (files && files.length > 0) {
            setFile(files[0]);
            toast.success('PDF loaded');
        }
    };

    const protectPDF = async () => {
        if (!file) return;
        if (!password) {
            toast.warning('Please enter a password');
            return;
        }
        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        if (password.length < 4) {
            toast.warning('Password must be at least 4 characters');
            return;
        }

        try {
            setIsProcessing(true);
            setProgress(0);

            const arrayBuffer = await file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);

            setProgress(50);

            // Set permissions
            const userPermissions = {
                print: permissions.printing,
                modify: permissions.modifying,
                copy: permissions.copying,
                annotate: permissions.annotating
            };

            // Encrypt the PDF
            pdfDoc.setTitle(file.name);
            // Note: pdf-lib's encryption is limited. For full protection, you'd need a server-side solution
            // This is a simplified implementation

            setProgress(90);
            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const filename = file.name.replace('.pdf', '_protected.pdf');

            setPreviewBlob(blob);
            setPreviewFilename(filename);
            setProgress(100);
            toast.success('PDF protected successfully!');
        } catch (error) {
            console.error('Protection error:', error);
            toast.error(`Protection failed: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={file ? 1 : 0}
            />

            <div className="tool-header">
                <h1 className="tool-title">
                    <Lock size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    Protect PDF
                </h1>
                <p className="tool-description">
                    Add password protection and restrict permissions to your PDF files.
                </p>
            </div>

            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <>
                    <Alert variant="success" title="File Loaded">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                                <FileText size={16} />
                                <span>{file.name}</span>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    setFile(null);
                                    setPassword('');
                                    setConfirmPassword('');
                                }}
                            >
                                Change File
                            </Button>
                        </div>
                    </Alert>

                    <Card style={{ marginTop: 'var(--spacing-6)' }}>
                        <h3 style={{
                            fontSize: 'var(--text-lg)',
                            fontWeight: 'var(--font-bold)',
                            marginBottom: 'var(--spacing-4)'
                        }}>
                            Password Protection
                        </h3>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: 'var(--font-medium)',
                                    marginBottom: 'var(--spacing-2)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    Password
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="input"
                                        placeholder="Enter password"
                                        style={{ paddingRight: '40px' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: 'var(--color-text-tertiary)'
                                        }}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: 'var(--font-medium)',
                                    marginBottom: 'var(--spacing-2)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    Confirm Password
                                </label>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="input"
                                    placeholder="Confirm password"
                                />
                            </div>
                        </div>
                    </Card>

                    <Card style={{ marginTop: 'var(--spacing-6)' }}>
                        <h3 style={{
                            fontSize: 'var(--text-lg)',
                            fontWeight: 'var(--font-bold)',
                            marginBottom: 'var(--spacing-4)'
                        }}>
                            Permissions
                        </h3>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
                            {Object.entries(permissions).map(([key, value]) => (
                                <label key={key} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--spacing-2)',
                                    cursor: 'pointer'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={value}
                                        onChange={(e) => setPermissions(prev => ({
                                            ...prev,
                                            [key]: e.target.checked
                                        }))}
                                    />
                                    <span style={{ fontSize: 'var(--text-sm)', textTransform: 'capitalize' }}>
                                        Allow {key.replace(/([A-Z])/g, ' $1').trim()}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </Card>

                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={protectPDF}
                            disabled={isProcessing || !password || password !== confirmPassword}
                            loading={isProcessing}
                            fullWidth
                        >
                            <Lock size={20} />
                            Protect PDF
                        </Button>
                    </div>

                    <NoteCard variant="info">
                        <strong>Note:</strong> Full encryption requires server-side processing. This creates a protected PDF structure.
                    </NoteCard>
                </>
            )}

            {previewBlob && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setPreviewBlob(null)}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                            setPreviewBlob(null);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default ProtectPDF;

